// Tenantra Inc. - Screening home: one row per address with its link status, fee and applicant pool
import { useMemo, useState } from 'react';
import { Plus, Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useSort, RowOpenButton } from './TableKit';
import { fmtDate } from './DetailPanel';
import { ScreeningLinkControls } from './ScreeningLinkSection';

const COLUMNS = [
  { key: 'address', label: 'Address', span: 4 },
  { key: 'link', label: 'Screening link', span: 2 },
  { key: 'fee', label: 'Fee', span: 1 },
  { key: 'applicants', label: 'Applicants', span: 2 },
  { key: 'top', label: 'Top score', span: 1 },
  { key: 'last', label: 'Last applied', span: 1 },
  { key: 'open', label: '', span: 1, sortable: false },
];
const ACC = { address: r => r.address, link: r => (r.link_active ? 0 : r.verified ? 1 : 2), fee: r => r.application_fee, applicants: r => r.applicants, top: r => r.top_score ?? -1, last: r => r.last_applied || '' };

export const Pill = ({ tone = 'slate', children, testId }) => {
  const t = { emerald: 'bg-emerald-50 text-emerald-700', slate: 'bg-slate-100 text-slate-600', amber: 'bg-amber-50 text-amber-700', teal: 'bg-teal-50 text-teal-700', red: 'bg-red-50 text-red-700' }[tone];
  const dot = { emerald: 'bg-emerald-500', slate: 'bg-slate-400', amber: 'bg-amber-500', teal: 'bg-teal-500', red: 'bg-red-500' }[tone];
  return <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] ${t}`} data-testid={testId}><span className={`w-1 h-1 rounded-full ${dot}`} />{children}</span>;
};

export const LightHeader = ({ columns, sort, onSort, lead }) => (
  <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-2.5 border-y border-slate-100 bg-slate-50/60 text-xs text-slate-500">
    {columns.map((c, i) => {
      const active = sort?.key === c.key;
      const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
      return c.sortable === false ? <div key={c.key} className={`col-span-${c.span}`}>{c.label}</div> : (
        <div key={c.key} className={`col-span-${c.span} flex items-center gap-2.5`}>{i === 0 && lead}<button type="button" onClick={() => onSort(c.key)} className={`group inline-flex items-center gap-1 text-left hover:text-slate-800 transition-colors ${active ? 'text-slate-800' : ''}`} data-testid={`sort-${c.key}`}>
          {c.label}<Icon className={`w-3 h-3 transition-opacity ${active ? 'opacity-80' : 'opacity-0 group-hover:opacity-50'}`} />
        </button></div>
      );
    })}
  </div>
);

export const StatStrip = ({ tiles }) => (
  <div className={`bg-white border border-slate-200 rounded-lg grid grid-cols-2 ${tiles.length > 1 ? 'lg:grid-cols-4' : ''} divide-slate-100 lg:divide-x`} data-testid="screening-stats">
    {tiles.map(t => (
      <div key={t.id} className="px-5 py-3.5" data-testid={`screening-stat-${t.id}`}>
        <p className="text-xs text-slate-500">{t.label}</p>
        <p className="text-xl font-medium tabular-nums text-slate-800 mt-1" data-testid={`screening-stat-${t.id}-value`}>{t.value}</p>
      </div>
    ))}
  </div>
);

function CreateLinkDialog({ open, onClose, addresses, onChanged }) {
  const [pid, setPid] = useState('');
  const options = addresses.filter(a => a.verified);
  const sel = options.find(a => a.property_id === pid);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setPid(''); } }}>
      <DialogContent className="sm:max-w-xl bg-white" data-testid="create-link-dialog">
        <DialogHeader><DialogTitle>Create a screening link</DialogTitle><DialogDescription>Pick the address, turn the link on and share it. Applicants for this address go into its own pool.</DialogDescription></DialogHeader>
        <select value={pid} onChange={(e) => setPid(e.target.value)} className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-md" data-testid="create-link-property">
          <option value="">Choose an address…</option>
          {options.map(a => <option key={a.property_id} value={a.property_id}>{a.address}{a.link_active ? ' (link already live)' : ''}</option>)}
        </select>
        {options.length === 0 && <p className="text-sm text-amber-700">None of your addresses are verified yet. Verify an address to take applications.</p>}
        {sel && <ScreeningLinkControls key={sel.property_id} propertyId={sel.property_id} verified compact onChange={onChanged} />}
      </DialogContent>
    </Dialog>
  );
}

const splitAddr = (a) => { const i = a.indexOf(','); return i < 0 ? [a, ''] : [a.slice(0, i), a.slice(i + 1).trim()]; };

function Row({ r, onOpen }) {
  const status = r.link_active ? ['Live', 'emerald'] : r.verified ? ['Off', 'slate'] : ['Verify address first', 'amber'];
  const [street, city] = splitAddr(r.address);
  return (
    <div onClick={onOpen} className="grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-slate-50/80 transition-colors cursor-pointer" data-testid={`address-row-${r.property_id}`}>
      <div className="col-span-10 sm:col-span-4 min-w-0">
        <p className="text-sm text-teal-700 truncate">{street}</p>
        <p className="text-xs text-slate-500 truncate">{city}{r.units ? ` · ${r.units} unit${r.units === 1 ? '' : 's'}` : ''}</p>
        <p className="sm:hidden text-[11px] text-slate-400">{status[0]} · {r.applicants} applicant{r.applicants === 1 ? '' : 's'}</p>
      </div>
      <div className="hidden sm:block col-span-2" data-testid={`address-link-${r.property_id}`}><Pill tone={status[1]}>{status[0]}</Pill></div>
      <div className="hidden sm:block col-span-1 text-sm text-slate-700 tabular-nums">{r.link_active || r.application_fee ? `$${Number(r.application_fee).toFixed(2)}` : '–'}</div>
      <div className="hidden sm:block col-span-2 text-sm text-slate-700" data-testid={`address-applicants-${r.property_id}`}>
        <span className="tabular-nums">{r.applicants}</span> in pool
        <p className="text-xs text-slate-400">{r.new_this_week ? <span className="text-teal-700">{r.new_this_week} new this week</span> : r.onboarding ? `${r.onboarding} in onboarding` : ' '}</p>
      </div>
      <div className="hidden sm:block col-span-1 text-sm tabular-nums text-slate-700">{r.top_score ?? '–'}</div>
      <div className="hidden sm:block col-span-1 text-sm text-slate-500">{r.last_applied ? fmtDate(r.last_applied) : '–'}</div>
      <div className="col-span-2 sm:col-span-1 flex justify-end"><RowOpenButton onClick={onOpen} testId={`address-open-${r.property_id}`} label={`Open screening for ${r.address}`} /></div>
    </div>
  );
}

export function ScreeningAddresses({ items, onOpen, onChanged }) {
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const filtered = useMemo(() => items.filter(r => !search || r.address.toLowerCase().includes(search.toLowerCase())), [items, search]);
  const { sorted, sort, toggle } = useSort(filtered, ACC);
  const live = items.filter(r => r.link_active).length;
  const tiles = [
    { id: 'live', label: 'Addresses taking applications', value: live },
    { id: 'applicants', label: 'Applicants in pools', value: items.reduce((s, r) => s + r.applicants, 0) },
    { id: 'new', label: 'New this week', value: items.reduce((s, r) => s + r.new_this_week, 0) },
    { id: 'onboarding', label: 'In onboarding', value: items.reduce((s, r) => s + r.onboarding, 0) },
  ];
  return (
    <div className="space-y-4" data-testid="screening-addresses">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-[15px] font-medium text-slate-800">Screening</h1></div>
        <button onClick={() => setCreating(true)} className="h-8 px-3 rounded-md bg-teal-600 hover:bg-teal-700 transition-colors text-white text-xs font-medium inline-flex items-center gap-1" data-testid="create-screening-link-btn"><Plus className="w-3.5 h-3.5" /> Create screening link</button>
      </div>
      <StatStrip tiles={tiles} />
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="address-table">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
          <p className="text-sm font-medium text-slate-800">Addresses</p>
          <div className="relative w-full sm:w-64"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input placeholder="Search addresses..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 pl-8 text-xs bg-white" data-testid="address-search" /></div>
        </div>
        {sorted.length === 0 ? (
          <div className="text-center py-10 border-t border-slate-100" data-testid="screening-empty"><p className="text-sm text-slate-700">{search ? 'No addresses match your search' : 'No addresses yet'}</p></div>
        ) : <>
          <LightHeader columns={COLUMNS} sort={sort} onSort={toggle} />
          <div className="divide-y divide-slate-100">{sorted.map(r => <Row key={r.property_id} r={r} onOpen={() => onOpen(r.property_id)} />)}</div>
          <div className="flex justify-between px-5 py-3 border-t border-slate-100 text-xs text-slate-500" data-testid="address-table-footer"><span>Showing {sorted.length} of {items.length} address{items.length === 1 ? '' : 'es'}</span></div>
        </>}
      </div>
      <CreateLinkDialog open={creating} onClose={() => setCreating(false)} addresses={items} onChanged={onChanged} />
    </div>
  );
}
